import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link as RouterLink } from "react-router-dom";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import {
  useMyMarketplaceListings,
  useUpdateMarketplaceListing,
  useUpdateMarketplaceListingStatus,
} from "../hooks/useMarketplaceApi";
import { MarketplaceListing } from "../marketplaceStorage";
import { useMutation } from "@apollo/client";
import { GENERATE_UPLOAD_URL } from "../../../../platform/graphql/mutations/image";
import { uploadToGCS } from "../../../../platform/util/upload/uploadToGCS";

const GIFT_CATEGORY_OPTIONS = [
  "Mobility & Safety",
  "Comfort & Bedding",
  "Memory Care Activities",
  "Daily Living Aids",
  "Wellness & Therapy",
  "Adaptive Clothing",
  "Entertainment & Games",
  "Personal Care",
  "Other",
] as const;

const AGE_GROUP_OPTIONS = [
  "All",
  "55+",
  "60+",
  "65+",
  "70+",
  "75+",
  "80+",
  "85+",
  "90+",
] as const;


const fileNameFromUrl = (url?: string | null) => {
  if (!url) return "";
  try {
    const last = decodeURIComponent(url.split("/").pop() || "");
    return (last.split("?")[0] || "").trim();
  } catch {
    return "";
  }
};

function money(n: number) {
  return `$${Number(n || 0).toLocaleString()}`;
}

function formatListingType(type?: string) {
  return type === "AFH_SALE" ? "AFH / Business For Sale" : "Gifts for Residents / Product";
}

function formatProductType(listing: MarketplaceListing) {
  if (listing.type !== "ELDER_GIFT") return "N/A";
  return String(listing.giftCategory || "").trim() || "Not set";
}

export default function MarketplaceManagePage() {
  const { providerUser, user } = React.useContext(UserContext) as any;
  const ownerId = String(providerUser?.userId || user?.userId || "");
  const { listings, loading, error, refetch } = useMyMarketplaceListings(ownerId);
  const { updateListing, loading: updatingListing } = useUpdateMarketplaceListing();
  const { updateStatus, loading: updating } = useUpdateMarketplaceListingStatus();
  const [mutGenerateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<MarketplaceListing | null>(null);
  const [form, setForm] = React.useState<any>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [imageIndexByListing, setImageIndexByListing] = React.useState<Record<string, number>>({});
  const imageInputRef = React.useRef<HTMLInputElement>(null);

  const getListingImages = React.useCallback((listing: MarketplaceListing) => {
    const images = Array.isArray(listing.imageUrls)
      ? listing.imageUrls.filter(Boolean)
      : [];
    if (images.length) return images;
    if (listing.giftImageUrl) return [listing.giftImageUrl];
    if (listing.imageUrl) return [listing.imageUrl];
    return [];
  }, []);

  const shiftListingImage = (listingId: string, total: number, delta: number) => {
    if (total <= 1) return;
    setImageIndexByListing((prev) => {
      const current = prev[listingId] || 0;
      const next = (current + delta + total) % total;
      return { ...prev, [listingId]: next };
    });
  };

  const openEdit = (listing: MarketplaceListing) => {
    setNotice(null);
    setEditing(listing);
    setForm({
      title: listing.title || "",
      description: listing.description || "",
      price: String(listing.price || ""),
      city: listing.city || "",
      state: listing.state || "",
      contactEmail: listing.contactEmail || "",
      contactPhone: listing.contactPhone || "",
      ownerBusinessName: listing.ownerBusinessName || "",
      beds: listing.beds == null ? "" : String(listing.beds),
      capacity: listing.capacity == null ? "" : String(listing.capacity),
      occupancy: listing.occupancy == null ? "" : String(listing.occupancy),
      giftCategory: GIFT_CATEGORY_OPTIONS.includes((listing.giftCategory || "") as any)
        ? listing.giftCategory || ""
        : listing.giftCategory
          ? "Other"
          : "",
      giftCategoryOther:
        listing.giftCategory && !GIFT_CATEGORY_OPTIONS.includes(listing.giftCategory as any)
          ? listing.giftCategory
          : "",
      ageGroup: listing.ageGroup || "",
      giftImageUrls:
        Array.isArray(listing.imageUrls) && listing.imageUrls.length
          ? listing.imageUrls
          : listing.giftImageUrl
            ? [listing.giftImageUrl]
            : [""],
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(null);
  };

  const changeStatus = async (id: string, status: "ACTIVE" | "PAUSED" | "SOLD") => {
    try {
      setNotice(null);
      await updateStatus(id, status);
      refetch();
    } catch (e: any) {
      setNotice(e?.message || "Failed to update listing status.");
    }
  };

  const saveEdit = async () => {
    if (!editing || !form) return;
    try {
      setNotice(null);
      const normalizedGiftImages = (form?.giftImageUrls || [])
        .map((u: any) => String(u || "").trim())
        .filter(Boolean);
      await updateListing(editing.id, {
        title: String(form.title || "").trim(),
        description: String(form.description || "").trim(),
        price: Number(form.price || 0),
        city: String(form.city || "").trim(),
        state: String(form.state || "").trim().toUpperCase(),
        contactEmail: String(form.contactEmail || "").trim(),
        contactPhone: String(form.contactPhone || "").trim() || null,
        ownerBusinessName:
          editing.type === "AFH_SALE"
            ? String(form.ownerBusinessName || "").trim() || null
            : undefined,
        beds:
          editing.type === "AFH_SALE" && form.beds !== ""
            ? Number(form.beds)
            : undefined,
        capacity:
          editing.type === "AFH_SALE" && form.capacity !== ""
            ? Number(form.capacity)
            : undefined,
        occupancy:
          editing.type === "AFH_SALE" && form.occupancy !== ""
            ? Number(form.occupancy)
            : undefined,
        giftCategory:
          editing.type === "ELDER_GIFT"
            ? String(
              form.giftCategory === "Other"
                ? form.giftCategoryOther || ""
                : form.giftCategory || ""
            ).trim() || null
            : undefined,
        ageGroup:
          editing.type === "ELDER_GIFT"
            ? String(form.ageGroup || "").trim() || null
            : undefined,
        giftImageUrl:
          editing.type === "ELDER_GIFT"
            ? normalizedGiftImages[0] || null
            : undefined,
        imageUrls:
          editing.type === "ELDER_GIFT"
            ? normalizedGiftImages
            : undefined,
      });
      closeEdit();
      refetch();
    } catch (e: any) {
      setNotice(e?.message || "Failed to update listing.");
    }
  };

  const capacityOptions = React.useMemo(() => {
    const max = Math.max(
      16,
      Number(form?.beds || 0),
      Number(form?.capacity || 0)
    );
    return Array.from({ length: max }, (_, i) => String(i + 1));
  }, [form?.beds, form?.capacity]);

  const occupancyOptions = React.useMemo(() => {
    const max = Math.max(0, Number(form?.capacity || form?.beds || 0));
    return Array.from({ length: max + 1 }, (_, i) => String(i));
  }, [form?.capacity, form?.beds]);

  const onPickGiftImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget;
    const file = inputEl.files?.[0] || null;
    if (!file) return;
    try {
      setUploadingImage(true);
      setNotice(null);
      const url = await uploadToGCS(file, mutGenerateUploadUrl);
      const clean = String(url || "").trim();
      if (clean) {
        setForm((p: any) => ({
          ...p,
          giftImageUrls: [...(p?.giftImageUrls || []), clean],
        }));
      }
    } catch (err: any) {
      setNotice(err?.message || "Failed to upload image.");
    } finally {
      setUploadingImage(false);
      inputEl.value = "";
    }
  };

  const removeGiftImageUrl = (index: number) => {
    setForm((p: any) => {
      const next = (p?.giftImageUrls || []).filter((_: string, i: number) => i !== index);
      return {
        ...p,
        giftImageUrls: next.length ? next : [""],
      };
    });
  };

  return (
    <Box sx={{ maxWidth: 920, mx: "auto", px: { xs: 1.5, sm: 2 }, py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          My Marketplace Listings
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={RouterLink} to="/care/marketplace" variant="outlined">
            View public marketplace
          </Button>
          <Button component={RouterLink} to="/care/marketplace/new" variant="contained">
            Post New Listing
          </Button>
        </Stack>
      </Stack>

      {!!error && (
        <Alert severity="warning" sx={{ mb: 1.2 }}>
          Backend unavailable for marketplace owner data.
        </Alert>
      )}
      {!!notice && (
        <Alert severity="error" sx={{ mb: 1.2 }}>
          {notice}
        </Alert>
      )}

      {!loading && listings.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button component={RouterLink} to="/care/marketplace/new" size="small">
              Post now
            </Button>
          }
        >
          No listings yet.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {listings.map((l: MarketplaceListing) => {
            const images = getListingImages(l);
            const currentIndex = Math.min(
              imageIndexByListing[l.id] || 0,
              Math.max(images.length - 1, 0)
            );
            return (
            <Card key={l.id} variant="outlined">
              {!!images.length && (
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    image={images[currentIndex]}
                    alt={l.title}
                    sx={{ height: 180, objectFit: "cover" }}
                  />
                  {images.length > 1 && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => shiftListingImage(l.id, images.length, -1)}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: 6,
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.45)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                        }}
                      >
                        <ChevronLeftRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => shiftListingImage(l.id, images.length, 1)}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: 6,
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.45)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                        }}
                      >
                        <ChevronRightRoundedIcon fontSize="small" />
                      </IconButton>
                      <Chip
                        size="small"
                        label={`${currentIndex + 1}/${images.length}`}
                        sx={{
                          position: "absolute",
                          right: 8,
                          bottom: 8,
                          bgcolor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                        }}
                      />
                    </>
                  )}
                </Box>
              )}
              <CardContent sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {l.title}
                    </Typography>
                  <Stack direction="row" spacing={0.75}>
                    <Chip size="small" label={l.type === "AFH_SALE" ? "AFH Sale" : "Gift"} />
                    {l.isExpired && (
                      <Chip size="small" color="error" label="EXPIRED" />
                    )}
                    <Chip
                      size="small"
                      color={l.status === "ACTIVE" ? "success" : l.status === "SOLD" ? "warning" : "default"}
                      label={l.status}
                    />
                  </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Type: {formatListingType(l.type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Product type: {formatProductType(l)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {money(l.price)} - {l.city}, {l.state}
                  </Typography>
                  {l.isExpired && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      Listing expired after 30 days. Post again to reactivate.
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => openEdit(l)}
                    >
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => changeStatus(l.id, "ACTIVE")} disabled={updating || !!l.isExpired}>
                      Mark Active
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => changeStatus(l.id, "PAUSED")} disabled={updating}>
                      Pause
                    </Button>
                    <Button size="small" variant="contained" onClick={() => changeStatus(l.id, "SOLD")} disabled={updating}>
                      Mark Sold
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
          })}
        </Stack>
      )}

      <Dialog open={!!editing} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          Edit Listing
          {!!editing && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Type: {formatListingType(editing.type)}
            </Typography>
          )}
          {!!editing && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Product type: {formatProductType(editing)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ pt: 0.8 }}>
            <TextField
              label="Title"
              value={form?.title || ""}
              onChange={(e) => setForm((p: any) => ({ ...p, title: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form?.description || ""}
              onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Price"
                type="number"
                value={form?.price || ""}
                onChange={(e) => setForm((p: any) => ({ ...p, price: e.target.value }))}
                fullWidth
              />
              <TextField
                label="City"
                value={form?.city || ""}
                onChange={(e) => setForm((p: any) => ({ ...p, city: e.target.value }))}
                fullWidth
              />
              <TextField
                label="State"
                value={form?.state || ""}
                onChange={(e) => setForm((p: any) => ({ ...p, state: e.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Contact email"
                value={form?.contactEmail || ""}
                onChange={(e) => setForm((p: any) => ({ ...p, contactEmail: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Contact phone"
                value={form?.contactPhone || ""}
                onChange={(e) => setForm((p: any) => ({ ...p, contactPhone: e.target.value }))}
                fullWidth
              />
            </Stack>
            {editing?.type === "AFH_SALE" ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Business name"
                  value={form?.ownerBusinessName || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, ownerBusinessName: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Beds"
                  type="number"
                  value={form?.beds || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, beds: e.target.value }))}
                  fullWidth
                />
                <TextField
                  select
                  label="Capacity (licensed)"
                  value={form?.capacity || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, capacity: e.target.value }))}
                  fullWidth
                >
                  {capacityOptions.map((v) => (
                    <MenuItem key={`edit-cap-${v}`} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Occupancy"
                  value={form?.occupancy || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, occupancy: e.target.value }))}
                  fullWidth
                >
                  {occupancyOptions.map((v) => (
                    <MenuItem key={`edit-occ-${v}`} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <TextField
                  select
                  label="Gift category"
                  value={form?.giftCategory || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, giftCategory: e.target.value }))}
                  fullWidth
                >
                  {GIFT_CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                {form?.giftCategory === "Other" && (
                  <TextField
                    label="Custom category"
                    value={form?.giftCategoryOther || ""}
                    onChange={(e) => setForm((p: any) => ({ ...p, giftCategoryOther: e.target.value }))}
                    fullWidth
                  />
                )}
                <TextField
                  select
                  label="Age group"
                  value={form?.ageGroup || ""}
                  onChange={(e) => setForm((p: any) => ({ ...p, ageGroup: e.target.value }))}
                  helperText="Optional"
                  fullWidth
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {AGE_GROUP_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Product images
                  </Typography>
                  <input
                    ref={imageInputRef}
                    id="gift-image-upload-input-edit"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onPickGiftImage}
                  />
                  <label htmlFor="gift-image-upload-input-edit">
                    <Button
                      size="small"
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileRoundedIcon />}
                      disabled={uploadingImage}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {uploadingImage ? "Uploading..." : "Add image"}
                    </Button>
                  </label>
                  {(form?.giftImageUrls || [""]).map((url: string, idx: number) => (
                    <Stack key={`gift-image-edit-${idx}`} direction="row" spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={url}
                        alt={`Gift ${idx + 1}`}
                        sx={{ width: 60, height: 60, objectFit: "cover", borderRadius: 1 }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                        {fileNameFromUrl(url) || `Image ${idx + 1}`}
                      </Typography>
                      <IconButton
                        aria-label="remove image"
                        onClick={() => removeGiftImageUrl(idx)}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={updatingListing}>
            {updatingListing ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
